
'use client';

import * as React from 'react';
import { cn } from '@shared/utils';
import { withPlatformComponents, WithPlatformComponents } from '../../platform';

/**
 * @group Components
 * 
 * @summary
 * <blockquote>Decorative separator icon component for section headings.</blockquote>
 * 
 * @example
 * ```tsx
 * <SeparatorIcon size={100} className="text-muted-foreground" />
 * ```
 */
interface SeparatorIconProps {
  size?: number;
  className?: string;
  color?: string;
}

const SeparatorIconComponent: React.FC<SeparatorIconProps & WithPlatformComponents> = ({
  size = 1000,
  color = 'currentColor',
  className,
  components
}) => {
  const { Svg, Path } = components;

  // Adjusted viewBox to fit only the icon content
  // Paths are in the bottom portion of the original 2200x2200 viewBox
  // Using a generous viewBox to ensure all path curves are visible
  // Horizontal: x=200 to x=2000 (width=1800)
  // Vertical: y=1720 to y=1820 (height=100) - generous range to capture all curves
  const viewBox = "200 1720 1800 200";
  const aspectRatio = 1800 / 200; // width / height = 12
  
  // Calculate height based on width to maintain aspect ratio
  const width = size;
  const height = size / aspectRatio;

  return (
    <Svg
      width={width}
      height={80}
      viewBox={viewBox}
      xmlns="http://www.w3.org/2000/svg"
      fill={color}
      className={cn("inline-block", className)}
      style={{ verticalAlign: 'middle' }}
    >
      <Path d="M257.443,1767.211c9.483-12.112,25.127-16.791,39.987-13.441c28.845,6.484,54.619,34.497,56.999,64.358    c1.453,16.256-6.399,32.31-18.6,42.9c-11.988,10.732-28.171,16.16-44.043,16.723c-31.764,0.903-63.308-13.789-82.381-39.653    c-9.666-13.046-14.753-29.422-15.469-45.643c-2.512-60.555,46.571-113.864,103.129-128.894    c62.576-16.178,130.387-2.253,187.981,24.578c77.098,38.945,147.325,91.93,229.964,118.633    c43.289,14.48,89.389,21.526,134.97,17.244c65.3-6.055,135.556-33.544,167.575-94.372c12.522-24.084,20.546-56.464,9.526-82.447    c1.821,3.134,3.28,6.502,4.401,9.98c3.293,10.531,4.021,21.701,3.287,32.693c-3.316,44.958-31.589,85.136-68.208,110.177    c-43.284,29.533-96.134,42.718-148.081,44.158c-67.148,1.577-132.908-20.073-192.348-49.573    c-52.302-25.328-100.142-58.241-151.912-83.87c-53.503-25.036-118.023-38.906-175.965-23.032    c-33.157,9.771-62.357,34.597-76.559,66.168c-8.025,17.84-10.516,37.943-5.291,56.754c5.384,18.699,19.763,33.392,37.029,42.569    c22.723,12.053,53.301,15.069,74.417-1.629c10.223-7.928,17.038-19.862,16.74-33.063c-0.389-20.498-13.301-39.62-29.484-51.662    C297.455,1753.736,274.03,1750.097,257.443,1767.211L257.443,1767.211z"/>
      <Path d="M1942.557,1767.211c-28.37-28.744-71.193,1.266-83.051,31.863c-6.455,15.215-5.672,33.294,5.652,45.794    c6.315,7.4,14.719,12.778,24.013,15.626c31.428,9.199,68.393-6.307,86.475-32.841c7.275-10.651,10.431-23.22,10.677-35.941    c0.375-48.425-39.658-90.709-84.579-103.98c-57.919-15.878-122.483-1.999-175.966,23.031    c-51.773,25.631-99.607,58.54-151.912,83.87c-65.968,32.864-140.227,55.728-214.708,48.268    c-72.593-6.876-151.74-40.01-182.979-110.233c-10.264-23.507-15.059-50.569-7.662-75.49c1.121-3.478,2.58-6.846,4.402-9.98    c-11.052,25.939-2.947,58.453,9.526,82.447c32.042,60.833,102.27,88.311,167.576,94.371c45.583,4.281,91.68-2.763,134.971-17.243    c82.645-26.707,152.859-79.686,229.963-118.634c57.606-26.835,125.393-40.753,187.981-24.578    c56.565,15.024,105.641,68.345,103.13,128.895c-0.721,16.219-5.795,32.599-15.47,45.642    c-19.061,25.866-50.624,40.554-82.38,39.653c-15.874-0.564-32.054-5.991-44.043-16.723c-12.215-10.602-20.04-26.631-18.6-42.9    c1.899-23.278,17.072-43.572,36.268-55.792C1901.202,1749.97,1926.998,1747.707,1942.557,1767.211L1942.557,1767.211z"/>
    </Svg>
  );
};

export const SeparatorIcon = withPlatformComponents(SeparatorIconComponent);